import { HitsiTunnus } from "./HitsiTunnus";
import { puuteKuvausTeksti } from "./puutekuvausTeksti";
import { useKohde, useKohdeSynkka } from "./Kohdekonteksti";
import Liitteet from "./Liitteet";
import { useKirjaustunniste } from "./kirjaustunniste";
import HitsiaEiTehda from "./HitsiaEiTehda";
import { TulosteKieli } from "./TulosteKieli";
import { naytaPvm } from "./pvm";
import { hitsinHuomio } from "./hitsihuomio";
import { t, esim, kieli, virheTeksti, type KaannosAvain } from "./kieli";
const tk = t;   // käytetään callbackeissa joissa `t` on paikallinen muuttuja
import { KohdeValinta } from "./KohdeValinta";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { nayttonimi } from "./nayttonimi";
import {
  api, ApiError, tanaan,
  type Kayttaja, type Kohde, type Linja, type Wps, type Hitsi, type Resurssi,
  type HitsauslokiTilanne, type Hitsityyppi, type SeuraavaNumero,
  HITSITYYPIT,
} from "./api";
import LinjanMateriaalit from "./LinjanMateriaalit";
import { HitsinKorjausKentalla } from "./HitsinKorjaus";

import { Ohje } from "./Ohje";
/* ═══════════════════════════════════════════════════════════
   Hitsausloki — kenttäkirjaus.

   Jäljitettävyys vaatii noin kaksitoista kenttää per hitsi. Se on
   puhelimessa mahdoton, jos ne naputellaan joka kerta.

   Ratkaisu on huomata mikä oikeasti muuttuu hitsien välillä:
   numero aina, sulatukset vasta kun putki vaihtuu. Kaikki muu on
   sama koko työvuoron.

   Siksi istunto asetetaan kerran ja hitsi on sen jälkeen YKSI
   PAINALLUS. Sulatuksen vaihto on erillinen tietoinen ele, koska
   juuri siinä kohtaa jäljitettävyys katkeaa jos se unohtuu.

   HYVÄKSYMISTESTI: jos tämä on hitaampi kuin nykyinen tapa, loki
   jää vajaaksi — ja vajaa jäljitettävyysdokumentti on huonompi kuin
   hauras mutta täysi.
   ═══════════════════════════════════════════════════════════ */

interface Istunto {
  linja_id: number;
  linja_tunnus: string;
  /**
   * Esivalmiste, asennus vai korjaus.
   *
   * Kysytään istunnon alussa, koska se ratkaisee mitkä numerot ovat
   * käytettävissä. Esivalmisteessa hitsattua saumaa ei voi kirjata
   * uudelleen asennuksessa — se oli todellinen ongelma kentällä ja
   * paisutti suoritemäärän.
   */
  tyyppi: Hitsityyppi;
  lehti: string;
  juuri: string;
  taytto: string;
  prosessi: string;
  wps_id: number | null;
  lisaaine: string;
  /** Sama koko putkelle: vaihtuu materiaalin mukana, ei hitsien välillä. */
  materiaali: string;
  dn: number | null;
  paiva: string;
}

const ISTUNTO_AVAIN = "sampo.hitsausloki.istunto";
/** Toni 10.10.2026 «kirjautuminen alkaa asetuslomakkeesta»: tallennettu istunto pyyhitään uloskirjautuessa ja «Vaihda
 *  linja» -napista. Ennen tätä «Vaihda linja» tyhjensi vain ruudun, ja sivun latauksen jälkeen vanha istunto palasi;
 *  uloskirjautuminen ei koskenut siihen lainkaan, joten jaetulla puhelimella seuraava kirjautuja jatkoi edellisen linjalla.
 *  Sivun uudelleenlataus ja vanhentunut istunto säilyttävät sen yhä (puhelin lataa sivun omin päin). */
export function tyhjennaHitsauslokinIstunto(): void {
  try {
    window.localStorage.removeItem(ISTUNTO_AVAIN);
    // Toni 10.10.2026 «A»: sulatukset eivät periydy seuraavalle kirjautujalle.
    for (const a of ["sampo.hitsausloki.sulatusmuisti", "sampo.hitsausloki.sulatukset", "sampo.hitsausloki.nimikkeet"]) window.localStorage.removeItem(a);
  } catch { /* yksityinen ikkuna: ei tallennettua */ }
}

/* Päivä ihmisen kielellä. Pelkkä "2026-08-18" ei kerro hitsarille
   onko se eilinen vai viime viikon — viikonpäivä kertoo. */
const VIIKONPAIVAT_ABL: KaannosAvain[] = ["hl.vp.su", "hl.vp.ma", "hl.vp.ti", "hl.vp.ke", "hl.vp.to", "hl.vp.pe", "hl.vp.la"];
const pvmLyhyt = (iso: string): string => {
  const [v, k, p] = iso.split("-");
  return `${Number(p)}.${Number(k)}.${v === String(new Date().getFullYear()) ? "" : v}`;
};
const pvmSanoin = (iso: string): string => {
  const d = new Date(iso + "T00:00:00Z");
  const vp = t(VIIKONPAIVAT_ABL[d.getUTCDay()]!);
  return `${vp} ${pvmLyhyt(iso)}`;
};
/** Montako vuorokautta sitten. Kalenteripäivinä, ei tunteina: kirjaus on
 *  päivä eikä hetki. */
const paiviaSitten = (iso: string): number => {
  const a = Date.parse(iso + "T00:00:00Z");
  const b = Date.parse(tanaan() + "T00:00:00Z");
  return Math.round((b - a) / 86400000);
};

/** Edellinen TYÖPÄIVÄ tästä päivästä: ma → pe, muuten eilinen.
 *
 *  ⛔ Lauantai ja sunnuntai jätetään väliin, koska maanantaina eilinen on
 *  sunnuntai eikä kukaan kirjaa sunnuntain työtä maanantaina. Ilman tätä
 *  maanantai kysyisi aina turhaan. Pyhäpäiviä EI tunneta — ne vaatisivat
 *  kalenterin, eikä sitä arvata. */
const edellinenTyopaiva = (): string => {
  const d = new Date(tanaan() + "T00:00:00Z");
  do { d.setUTCDate(d.getUTCDate() - 1); } while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
};

/* ── Sulatusmuisti (Toni 10.10.2026 «A» + Coworkin lisä: kysymys vain kun asiayhteys vaihtuu) ─────────────────────────
   Ennen tätä sulatukset ja nimikkeet olivat puhelimen muistissa ilman asiayhteyttä: ne periytyivät toisen hitsaajan,
   toisen linjan ja uloskirjautumisen yli. Jaetulla puhelimella luovutusaineistoon saattoi tulla oikean näköinen mutta
   väärä sulatusnumero. Nyt muisti kantaa kirjaajan, linjan, lehden ja hetken:
     · sama kirjaaja, linja ja lehti, alle SULATUS_TAUKO_H tuntia → periytyy kuten ennen (yksi painallus per hitsi)
     · muuten periytynyt arvo kysytään («Sama sulatus kuin edellisessä?»), ja kirjaus odottaa vastausta
     · osan nimikkeen vaihtuessa sen osan sulatus tyhjenee (eri putki tai osa → eri sulatus)
     · uloskirjautuminen pyyhkii muistin (tyhjennaHitsauslokinIstunto)
   Vanha muisti (avaimet ilman asiayhteyttä) luetaan kerran kysyttävänä ja poistetaan. */
const SULATUS_AVAIN = "sampo.hitsausloki.sulatukset";
const NIMIKE_AVAIN = "sampo.hitsausloki.nimikkeet";
const SULATUSMUISTI_AVAIN = "sampo.hitsausloki.sulatusmuisti";
/** Pitkä tauko: yli tämän edellisestä kirjauksesta periytynyt sulatus kysytään. Rakentajan valinta 10.10.2026 (Tonin
 *  vahvistettava): yön yli ja pitkä keskeytys kysytään, ruokatauko ei. */
export const SULATUS_TAUKO_H = 4;
type Sulatusmuisti = { kayttaja_id: number | null; linja_id: number | null; lehti: string | null;
                       nimikkeet: [string, string]; sulatukset: [string, string]; hetki: number };
export type SulatusMuutos = "kirjaaja" | "linja" | "lehti" | "tauko";
function lueSulatusmuisti(): Sulatusmuisti | null {
  const m = lueTallennettu<Sulatusmuisti>(SULATUSMUISTI_AVAIN);
  if (m && Array.isArray(m.sulatukset)) return m;
  // Vanha muoto: arvot ilman asiayhteyttä → kysytään kerran (kirjaaja tuntematon).
  const s = lueTallennettu<[string, string]>(SULATUS_AVAIN), n = lueTallennettu<[string, string]>(NIMIKE_AVAIN);
  try { window.localStorage.removeItem(SULATUS_AVAIN); window.localStorage.removeItem(NIMIKE_AVAIN); } catch { /* */ }
  return s || n ? { kayttaja_id: null, linja_id: null, lehti: null, nimikkeet: n ?? ["", ""], sulatukset: s ?? ["", ""], hetki: 0 } : null;
}
/** Mikä asiayhteydessä vaihtui, tai null jos periytyminen on hiljainen. */
export function sulatusmuistinMuutos(m: Sulatusmuisti | null, kayttajaId: number, linjaId: number, lehti: string, nyt: number): SulatusMuutos | null {
  if (!m || !(m.sulatukset[0] || m.sulatukset[1])) return null;
  if (m.kayttaja_id !== kayttajaId) return "kirjaaja";
  if (m.linja_id !== linjaId) return "linja";
  if (m.lehti !== lehti) return "lehti";
  if (nyt - m.hetki > SULATUS_TAUKO_H * 3_600_000) return "tauko";
  return null;
}

// Osalaji valintana, ei vapaana tekstinä: vapaa teksti tuottaa kymmenen
// kirjoitusasua samasta asiasta eikä niistä voi laskea. "Muu" avaa
// tekstikentän sille mitä listalla ei ole.
const NIMIKKEET = [
  "Putki", "Käyrä", "Laippa", "T-haara", "Venttiili",
  "Supistus", "Kartio", "Tulppa",
];
/* T439: arvo tallentuu suomeksi kuten ennenkin (vanhat rivit samalla arvolla); vain esitys käännetään.
   Päätöksen 105 tunnisteiksi muunto on oma eränsä (vaihe c). */
const NIMIKKEEN_AVAIN: Record<string, KaannosAvain> = {
  Putki: "hl.nimike.putki", "Käyrä": "hl.nimike.kayra", Laippa: "hl.nimike.laippa", "T-haara": "hl.nimike.t_haara",
  Venttiili: "hl.nimike.venttiili", Supistus: "hl.nimike.supistus", Kartio: "hl.nimike.kartio", Tulppa: "hl.nimike.tulppa",
};
const nimikeTeksti = (n: string): string => (NIMIKKEEN_AVAIN[n] ? t(NIMIKKEEN_AVAIN[n]!) : n);
/** Hitsin lajin nimi (A/E/R/S) käyttäjän kielellä; arvo on tunniste. */
const LAJIN_AVAIN: Record<string, KaannosAvain> = { A: "hl.laji.A", E: "hl.laji.E", R: "hl.laji.R", S: "hl.laji.S" };
/** T529 (12.10.2026): kirjaimen nimi selitteeseen. S:n nimi on Selailun «sel.laji_s» (lisätesti) — hl.laji.S on pelkkä «S». */
const lajinSelite = (x: string): string => (x === "S" ? t("sel.laji_s") : LAJIN_AVAIN[x] ? t(LAJIN_AVAIN[x]!) : x);
// Suomeksi esitys pysyy täsmälleen ennallaan (istuntorivillä «A», valinnassa «Asennus»); käännös vain muille kielille.
const lajinNimi = (arvo: string, fi?: string): string =>
  kieli() === "fi" || !LAJIN_AVAIN[arvo] ? (fi ?? arvo) : t(LAJIN_AVAIN[arvo]!);

/** T654 (Toni 25.10.2026 klo 20.15): kenttätekijän napit puhelinkokoisina — 44 px korkea, 16 px teksti, ei rivity eikä leikkaudu. */
const KENTTANAPPI = { minHeight: 44, fontSize: 16, padding: "8px 14px", whiteSpace: "nowrap" as const, flex: "0 0 auto" };

/** Istunto säilyy sivun latauksen yli: puhelimessa selain lataa
 *  sivun uudelleen omin päin, eikä asetuksia jaksa syöttää kahdesti. */
function lueTallennettu<T>(avain: string): T | null {
  try {
    const s = window.localStorage.getItem(avain);
    return s ? (JSON.parse(s) as T) : null;
  } catch { return null; }
}
function tallenna(avain: string, arvo: unknown) {
  try { window.localStorage.setItem(avain, JSON.stringify(arvo)); } catch { /* tila täynnä */ }
}

export default function Hitsausloki({
  kayttaja, osoiteKohde, asetaOsoiteKohde,
}: {
  kayttaja: Kayttaja;
  /** Osoitteen `#/hitsausloki/<linjatunnus>` -hanta, tai null. */
  osoiteKohde?: string | null;
  asetaOsoiteKohde?: (tunnus: string | null) => void;
}) {
  /*
   * Kuka saa kirjata hitsin.
   *
   * TÄMÄN LISTAN ON OLTAVA SAMA KUIN `POST /api/hitsit` (routes.ts):
   * paakayttaja, suunnittelija, tyonjohtaja, kenttatekija (ennen hitsaaja, migraatio 126).
   *
   * Oli [paakayttaja, suunnittelija] ja nimeltään `saaMuokata` — ja juuri
   * nimi oli puoli vikaa: muokkausoikeuden lippua käytettiin
   * kirjausoikeuden porttina. Seuraus oli hiljainen: hitsaaja ja
   * työnjohtaja näkivät "Ei oikeutta kirjata" vaikka API olisi
   * ottanut kirjauksen vastaan, ja tämän näkymän oma hitsaajatuki
   * (`onHitsaaja`, `lukitseHitsaaja`) oli saavuttamatonta koodia.
   * Löytyi ensimmäisessä puhelintestissä oikealla hitsaajatunnuksella.
   *
   * Selain tiukempi kuin API = kuollutta toiminnallisuutta. Löysempi =
   * ruma virhe käyttäjälle. Kumpikaan ei ole vaaraton, ja kumpaakaan ei
   * tarkista mikään — ks. docs/PAATOKSET.md §Selaimen rooliportti ja
   * reitin vaadiRooli ovat sama sopimus.
   */
  const saaKirjata = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja", "kenttatekija"].includes(r));
  // Kenttätekijä kirjautuu omalla tunnuksellaan: koodi tulee automaattisesti
  // (henkilölinkistä) eikä häntä valita listasta. Hitsauslokissa koodi on hitsaajan leima.
  const onHitsaaja = kayttaja.roolit.includes("kenttatekija") && !!kayttaja.kenttakoodi;
  /* 207 (pääChatin toimeksianto Tonin välittämänä 8.10.2026, Norjan erä): peruskenttätili ei näe työnjohdon sisältöä —
     tulosteita, kohteen puutelukua eikä «Mikä on jumissa». Sama ehto kuin API:n `peruskenttatili` (auth.ts). */
  const peruskenttatili = kayttaja.roolit.includes("kenttatekija")
    && !kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja", "lukija"].includes(r))
    && !kayttaja.oikeudet.includes("hitsausloki_tyonjohto");
  const [tulosteKieli, setTulosteKieli] = useState("");

  const [istunto, setIstunto] = useState<Istunto | null>(() => lueTallennettu<Istunto>(ISTUNTO_AVAIN));
  const [linjaViesti, setLinjaViesti] = useState<string | null>(null);
  /*
   * Tarran kiinteä etuliite. Haetaan vain kun istunto on käynnissä:
   * Aloitus hakee saman tilannekuvan itse, joten aloitusnäkymässä tämä
   * olisi turha toinen pyyntö puhelinverkossa.
   */
  const [tarraEtuliite, setTarraEtuliite] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);

  /*
   * Istunto säilyy localStoragessa yli päivien. Se on tarkoituksellista:
   * perjantain hitsien kirjaaminen maanantaina on laillinen ja yleinen
   * työtapa, eikä sitä saa estää — pakko ei poista tarvetta, se piilottaa
   * sen. Hitsari kirjaisi perjantain työn maanantain päivällä, ja data
   * muuttuisi huonommaksi samalla kun se näyttäisi siistimmältä.
   *
   * Siksi kysymys eikä este: oletus on tämä päivä, vanha päivä on
   * valittavissa NIMELTÄ. Kysytään vain kerran per istunto-päivä-pari.
   */
  const [paivaKysytty, setPaivaKysytty] = useState(false);
  /* ⛔ Kysytään VAIN jos istunnon päivä on edellinen työpäivä (Toni 21.9.2026).
     Vanhempi istunto ei ole kysymys vaan jäänne: «yhä 27.8. työtä» on
     25 päivän päästä lähes aina väärä valinta, mutta näytti yhtä
     tärkeältä kuin oikea. Silloin oletus on tänään ilman kysymystä, ja
     muun päivän voi valita erikseen. */
  const [eiTehdaAuki, setEiTehdaAuki] = useState(false);
  const [eiTehdaViesti, setEiTehdaViesti] = useState<string | null>(null);
  const vanhaPaiva = !!istunto && istunto.paiva !== tanaan()
    && istunto.paiva >= edellinenTyopaiva() && !paivaKysytty;
  /* Yli työpäivän vanha istunto siirtyy tähän päivään itsestään. Tehdään
     kerran ja merkitään kysytyksi, jotta vaihto ei toistu joka renderillä. */
  useEffect(() => {
    if (istunto && istunto.paiva !== tanaan() && istunto.paiva < edellinenTyopaiva()) {
      setIstunto({ ...istunto, paiva: tanaan() });
      setPaivaKysytty(true);
    }
  }, [istunto]);

  useEffect(() => {
    if (istunto) tallenna(ISTUNTO_AVAIN, istunto);
  }, [istunto]);

  /*
   * Istunnon linja menee osoitteeseen: kirjauksen aikana AVOIN ASIA
   * on se linja jota kirjataan. Ilman istuntoa hanta jaa pois, ja
   * `Aloitus` kayttaa sita esivalintana.
   *
   * ⚠ Tama hook on TASSA eika lahempana kayttokohtaansa, koska
   * rivilla ~150 on `if (!saaKirjata) return`. Hook sen jalkeen
   * ajaisi vain toisella haaralla -- se on se vika joka kaatoi
   * nelja instanssia 41 tunniksi. `rules-of-hooks` vartioi tata
   * nyt kaannoksessa.
   */
  useEffect(() => {
    asetaOsoiteKohde?.(istunto ? istunto.linja_tunnus : null);
  }, [istunto]);

  useEffect(() => {
    if (!istunto) return;
    api.hitsauslokiTilanne()
      .then((t) => setTarraEtuliite(t.tarra_etuliite ?? ""))
      .catch(() => setTarraEtuliite(""));
  }, [istunto]);

  if (!saaKirjata) {
    return (
      <div className="tyhja">
        {/* h1 myös oikeushaarassa — ks. Ainestodistukset. */}
        <h1>{t("hl.ei_oikeutta")}</h1>
        <p>{t("hl.ei_oikeutta_selite")}</p>
      </div>
    );
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("valikko.hitsausloki")}</div>
        <div className="head__rivi">
          {/* T654 (Toni 25.10.2026 klo 20.15): «Vaihda linja» linjatunnuksen vieressä — linjaa vaihdetaan siitä, missä linja
              näkyy, ei työnjohdon tulosteiden seasta. Puhelinkoko 44 px / 16 px, teksti ei rivity eikä leikkaudu. */}
          <div data-hl-otsikko="" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
            <h1>{istunto ? istunto.linja_tunnus : t("hl.aloita_kirjaus")}</h1>
            {istunto && (
              <button className="nappi nappi--kevyt" data-vaihda-linja="" style={KENTTANAPPI}
                      onClick={() => { tyhjennaHitsauslokinIstunto(); setIstunto(null); }}>
                {t("hl.vaihda_linja")}
              </button>
            )}
          </div>
          {eiTehdaViesti && (
            <p className="huom" role="status">{eiTehdaViesti}</p>
          )}
          {istunto && eiTehdaAuki && (
            <HitsiaEiTehda
              linjaId={istunto.linja_id}
              linjaTunnus={istunto.linja_tunnus}
              sulje={() => setEiTehdaAuki(false)}
              valmis={(t) => {
                setEiTehdaAuki(false);
                /* Tila sanotaan ääneen: «esitys» ja «merkitty» ovat eri
                   asioita, eikä kenttätekijän pidä luulla päättäneensä. */
                setEiTehdaViesti(t.tila === "esitys" ? tk("hl.ei_tehda.esitys") : tk("hl.ei_tehda.merkitty"));
              }}
            />
          )}
          {linjaViesti && <p className="huom" role="alert">{linjaViesti}</p>}
          <div className="head__ohjaus">
            {/* Vienti on saatavilla aina, myös kesken kirjauksen:
                palaveri voi tulla eteen milloin tahansa. */}
            {/* Kaksi eri dokumenttia, ei kaksi muotoilua samasta:
                työnjohdon tuloste kertoo missä mennään, virallinen
                loki on jäljitettävyysdokumentti tarkastuslaitokselle. */}
            {!peruskenttatili && (
            <a className="nappi nappi--kevyt" data-hl-tuloste="tyonjohto"
               href={api.hitsauslokiVienti({
                 ...(istunto ? { linja_id: istunto.linja_id } : {}),
                 ...(tulosteKieli ? { kieli: tulosteKieli } : {}),
               })}>
              {t("hl.tyonjohdon_tuloste")}
            </a>)}
            {/* Virallinen loki ei kuulu kenttätyölle (API 403, Toni 22.9.2026) — nappi näkyi silti. */}
            {!kayttaja.roolit.every((r) => r === "kenttatekija") && (<>
            <a className="nappi nappi--kevyt" data-hl-tuloste="virallinen"
               href={api.hitsauslokiVirallinen({
                 ...(istunto ? { linja_id: istunto.linja_id } : {}),
                 ...(tulosteKieli ? { kieli: tulosteKieli } : {}),
               })}>
              {t("hl.virallinen_loki")}
            </a>
            {/* Vaihe c (209): kieli valitaan tulostushetkellä, oletus kohteen dokumentaatiokieli. */}
            <TulosteKieli arvo={tulosteKieli} muuta={setTulosteKieli} />
            </>)}
          </div>
        </div>
      </header>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {/* ⛔ NAPIT EIVÄT OLE SAMANARVOISIA (Toni 21.9.2026). Tänään on
          oletus ja korostettu; edellisen päivän jatkaminen on toissijainen
          ja kertoo ikänsä napissa. Kysymys on otsikko eikä virke: «Kirjaatko
          yhä sen päivän työtä, vai tämän päivän?» piti lukea kahdesti.

          Päiväys ilman perässä tulevaa pistettä: `pvmLyhyt` päättyy jo
          pisteeseen kuluvana vuonna (27.8.), ja lauseen oma piste teki
          siitä «27.8..». */}
      {vanhaPaiva && istunto && (
        <div className="huom" role="alert">
          <p style={{ margin: "0 0 10px", fontWeight: 600 }}>{t("hl.mille_paivalle")}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button className="nappi nappi--paa" onClick={() => {
              setIstunto({ ...istunto, paiva: tanaan() });
              setPaivaKysytty(true);
            }}>
              {t("hl.tanaan")} {pvmLyhyt(tanaan())}
            </button>
            <button className="nappi nappi--kevyt" onClick={() => setPaivaKysytty(true)}>
              {t("hl.jatka_edellista", { pvm: pvmLyhyt(istunto.paiva), n: paiviaSitten(istunto.paiva) })}
            </button>
            <MuuPaiva valitse={(p) => {
              setIstunto({ ...istunto, paiva: p });
              setPaivaKysytty(true);
            }} />
          </div>
        </div>
      )}

      {/* Jälkikirjaus ilman kysymystä: päivä näkyy aina ja on vaihdettavissa,
          myös silloin kun istunto on tältä päivältä. Ilman tätä yli työpäivän
          vanha istunto siirtyisi tähän päivään eikä muuta päivää saisi
          valittua mistään. */}
      {istunto && !vanhaPaiva && (
        <p className="dim" style={{ margin: "0 0 10px" }}>
          {t("hl.kirjataan_paivalle")} <b>{pvmSanoin(istunto.paiva)}</b>{" "}
          <MuuPaiva valitse={(p) => setIstunto({ ...istunto, paiva: p })} />
        </p>
      )}

      {istunto
        ? <>
            <Kirjaus istunto={istunto} tarraEtuliite={tarraEtuliite} kayttajaId={kayttaja.id} eiTehda={() => setEiTehdaAuki(true)}
                     linjaKadonnut={() => {
                       // Linja on poistettu kannasta istunnon aikana (tai puhdistuksessa).
                       // Tallennettu istunto pyyhitään, muuten se palaa joka latauksessa.
                       try { window.localStorage.removeItem(ISTUNTO_AVAIN); } catch { /* ei väliä */ }
                       setIstunto(null);
                       setLinjaViesti(t("hl.linja_poistunut", { linja: istunto.linja_tunnus }));
                     }} />
            {/* Liitteen lisäys on eri oikeus kuin kirjaus — hitsaaja näkee
                isometrin muttei lisää sitä. Lisäyslista vastaa
                POST /api/liitteet -reittiä (työnjohtaja mukaan kun kohde on
                hitsauslokin domainia); poisto on tiukempi ja kulkee omana
                lippunaan LinjanMateriaalit → Liitteet. */}
            <LinjanMateriaalit linjaId={istunto.linja_id} tunnus={istunto.linja_tunnus}
              saaLisata={kayttaja.roolit.some((r) =>
                ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r))}
              saaPoistaa={kayttaja.roolit.some((r) =>
                ["paakayttaja", "suunnittelija"].includes(r))} />
          </>
        : kayttaja.vainPari
        ? <p className="panel" data-vain-pari="" style={{ fontSize: 15 }}>{t("hl.vain_pari")}</p>
        : <Aloitus valmis={setIstunto} virhe={setVirhe}
                   hitsaajaKoodi={kayttaja.kenttakoodi} lukitseHitsaaja={onHitsaaja}
                   esivalinta={osoiteKohde ?? null} />}
    </>
  );
}

/* ── Istunnon asetus ────────────────────────────────────── */

function Aloitus({
  valmis, virhe, hitsaajaKoodi, lukitseHitsaaja, esivalinta,
}: {
  valmis: (i: Istunto) => void;
  virhe: (v: string | null) => void;
  /** Osoitteesta tullut linjatunnus, esivalittavaksi. */
  esivalinta?: string | null;
  /** Hitsaajan oma koodi esitäyttöön (null = ei kytketty). */
  hitsaajaKoodi: string | null;
  /** Hitsaaja ei valitse itseään: kenttä lukitaan hänen koodiinsa. */
  lukitseHitsaaja: boolean;
}) {
  const [tilanne, setTilanne] = useState<HitsauslokiTilanne | null>(null);
  // Vakiokoot DN-valintaan. Tyhjä lista = kanta ei vielä vastannut;
  // kenttä on silloin tyhjä valinta eikä tekstikenttä, joten väärää
  // arvoa ei pääse syntymään odottaessakaan.
  const dnKoot = tilanne?.dn_koot ?? [];
  // Lomake on tilannekuvan alapuolella, usein ruudun ulkopuolella.
  // Ilman vieritystä valinta näyttää siltä ettei mitään tapahtunut.
  const lomake = useRef<HTMLElement>(null);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [linjat, setLinjat] = useState<Linja[]>([]);
  const [wpsLista, setWpsLista] = useState<Wps[]>([]);
  const [hitsaajat, setHitsaajat] = useState<Resurssi[]>([]);
  const [asentajat, setAsentajat] = useState<Resurssi[]>([]);

  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T426: yläpalkin kohdevalinta ohjaa. Allokaation työmaa (rivi 91) esivalitaan vain kun yläpalkin valinta ei ole
  // käytössä — muuten valinta on yläpalkissa eikä kahta lähdettä ole.
  const { ylapalkissa, synkattu } = useKohdeSynkka(kohdeId, setKohdeId, { kaikkiSallittu: true });
  const [allokaationTyomaa, setAllokaationTyomaa] = useState<number | null>(null);
  useEffect(() => { if (allokaationTyomaa && !ylapalkissa) setKohdeId(allokaationTyomaa); }, [allokaationTyomaa, ylapalkissa]);
  // Rivi 91: työmaa allokaatiosta oletukseksi — suodatin, ei kielto; käyttäjä voi vaihtaa.
  const [tyomaaVihje, setTyomaaVihje] = useState<string | null>(null);
  const [haku, setHaku] = useState("");
  const [linja, setLinja] = useState<Linja | null>(null);

  const edellinen = lueTallennettu<Istunto>(ISTUNTO_AVAIN);
  const [tyyppi, setTyyppi]     = useState<Hitsityyppi>(edellinen?.tyyppi ?? "A");
  const [lehti, setLehti]       = useState("");
  // Hitsaajan oletus (pääChatin toimeksianto Tonin välittämänä 7.10.2026; nykytila raportoitu ensin): kenttätili on aina
  // itse — edellisen istunnon arvo (jaettu puhelin) ei saa ohittaa lukittua kenttää. Toimiston käyttäjä aloittaa tyhjästä ja
  // valitsee listasta: hänen oma henkilökoodinsa ei ole hitsaaja.
  const [juuri, setJuuri]       = useState(lukitseHitsaaja ? (hitsaajaKoodi ?? "") : "");
  const [taytto, setTaytto]     = useState(edellinen?.taytto ?? "");
  const [prosessi, setProsessi] = useState(edellinen?.prosessi ?? "141");
  const [wpsId, setWpsId]       = useState<number | "">(edellinen?.wps_id ?? "");
  const [lisaaine, setLisaaine] = useState(edellinen?.lisaaine ?? "");
  const [materiaali, setMateriaali] = useState(edellinen?.materiaali ?? "");
  // DN EI periydy edellisestä istunnosta: se kuuluu linjalle. Jos se
  // jäisi voimaan linjaa vaihdettaessa, jokainen hitsi saisi väärän
  // dia_inch-painotuksen eikä mikään kertoisi siitä.
  const [dn, setDn]             = useState("");
  // 7.10.2026: linjan isometreistä luettavat valinnat. Lehti: Vertex-kokoonpanon sivut n/m sivujärjestyksessä (nimiössä ei ole
  // arkkitietoa), Swecon kuvilla niiden lehdet; yksi vaihtoehto täyttyy itse (1/1). Materiaali: osaluettelon materiaalit.
  const [lehdet, setLehdet] = useState<string[] | null>(null);
  const [materiaalit, setMateriaalit] = useState<{ arvo: string; mitat: string[] }[]>([]);
  const [lehtiMuu, setLehtiMuu] = useState(false);

  useEffect(() => {
    Promise.all([api.kohteet(), api.wpsLista(), api.resurssit(), api.minunTyomaa().catch(() => null)])
      .then(([k, w, h, t]) => {
        setKohteet(k); setWpsLista(w);
        if (t?.kohde_id && k.some((x) => x.id === t.kohde_id)) {
          setAllokaationTyomaa(t.kohde_id);
          setTyomaaVihje(tk("hl.tyomaa_allokaatiosta", { tunnus: t.tunnus ?? "", peruste: t.peruste }));
        } else if (k.length === 0 && t) {
          // Tyhjä kohdelista ilman syytä näyttää rikkinäiseltä (13.9.2026): kerrotaan miksi työmaata ei näy.
          setTyomaaVihje(tk("hl.ei_tyomaata", { peruste: t.peruste }));
        }
        // Kaksi listaa, eri osaamisilla. Kolmenkymmenen nimen
        // listasta oikean löytäminen puhelimella on hidasta ja
        // virhealtista, ja osaaminen on jo kannassa — tämä on
        // suodatin, ei uusi tieto.
        const omat = h.filter((x) => !x.nimeamaton);
        setHitsaajat(omat.filter((x) => x.osaamiset.includes("Hitsaaja")));
        setAsentajat(omat.filter((x) =>
          x.osaamiset.some((o) => o.includes("asentaja") || o.includes("Asentaja"))));
      })
      .catch(() => virhe(t("hl.perustiedot_virhe")));
  }, [virhe]);

  // T656 (Toni 25.10.2026 klo 13.20): etusivun luvut ja «Mikä on jumissa» yläpalkin kohteesta, kuten Hub.
  // Ei haeta ennen kuin näkymän kohde vastaa yläpalkkia (T472: muuten ensin «kaikki kohteet» ja sitten valittu).
  useEffect(() => {
    if (!synkattu) return;
    let voimassa = true;
    api.hitsauslokiTilanne(kohdeId === "" ? undefined : kohdeId)
      .then((t) => { if (voimassa) setTilanne(t); })
      .catch(() => { if (voimassa) setTilanne(null); });
    return () => { voimassa = false; };
  }, [kohdeId, synkattu]);

  // Haku käynnistyy kolmesta merkistä: lyhyempi tuottaa liikaa osumia
  // eikä auta valinnassa.
  useEffect(() => {
    if (haku.length < 2 && kohdeId === "") { setLinjat([]); return; }
    const t = setTimeout(() => {
      api.linjat(haku || undefined, kohdeId === "" ? undefined : kohdeId)
        .then(setLinjat)
        .catch(() => setLinjat([]));
    }, 250);
    return () => clearTimeout(t);
  }, [haku, kohdeId]);

  /*
   * Osoitteesta tullut linja esivalitaan.
   *
   * Kaksi vaihetta, koska `linjat` latautuu vasta kun haussa on
   * merkkeja: ensin siemennetaan haku tunnuksella, sitten valitaan
   * osuma kun lista saapuu.
   *
   * ⛔ Jaettu linkki ei putoa hiljaa: jos tunnusta ei loydy, naytolle
   * tulee nakyva syy. Sama saanto kuin Selailussa -- oletukseen
   * palaaminen kertoisi vastaanottajalle etta linkki toimi.
   */
  const [esivalintaVirhe, setEsivalintaVirhe] = useState<string | null>(null);
  const [esivalintaTehty, setEsivalintaTehty] = useState(false);
  useEffect(() => {
    if (esivalinta && !esivalintaTehty) setHaku(esivalinta);
  }, [esivalinta]);
  useEffect(() => {
    if (!esivalinta || esivalintaTehty || linjat.length === 0) return;
    const osumat = linjat.filter((l) => l.tunnus === esivalinta);
    setEsivalintaTehty(true);
    if (osumat.length === 1) { setEsivalintaVirhe(null); valitseLinja(osumat[0]!); }
    else if (osumat.length === 0)
      setEsivalintaVirhe(t("hl.esivalinta_ei_loydy", { linja: esivalinta }));
    else
      setEsivalintaVirhe(t("hl.esivalinta_monta", { linja: esivalinta, n: osumat.length }));
  }, [linjat, esivalinta]);

  // Lehti on pakollinen jos linjalla on isometri: numerointi on
  // lehtikohtaista, ja ilman lehteä eri lehtien samat numerot
  // sulautuisivat yhdeksi.
  const lehtiPuuttuu = !!linja?.isometri_olemassa && !lehti.trim();
  // Materiaali on jäljitettävyyden toinen puoli: sulatusnumero yksin
  // ei kerro mitä ainetta se on, eikä ainestodistusta voi täsmäyttää.
  const materiaaliPuuttuu = !materiaali.trim();

  function aloita() {
    if (!linja || lehtiPuuttuu || materiaaliPuuttuu) return;
    valmis({
      linja_id: linja.id,
      linja_tunnus: linja.tunnus,
      tyyppi,
      lehti: lehti.trim() || "-",
      juuri: juuri.trim(),
      taytto: taytto.trim(),
      prosessi: prosessi.trim(),
      wps_id: wpsId === "" ? null : wpsId,
      lisaaine: lisaaine.trim(),
      materiaali: materiaali.trim(),
      dn: dn ? Number(dn) : (linja.dn_nimellinen ?? null),
      paiva: tanaan(),
    });
    virhe(null);
  }

  /** Yksi paikka linjan valinnalle: täyttö ja vieritys aina yhdessä. */
  const valitseLinja = useCallback((l: Linja) => {
    setLinja(l);
    setHaku(l.tunnus);
    setLehti(l.viim_lehti && l.viim_lehti !== "-" ? l.viim_lehti : "");
    setLehdet(null); setMateriaalit([]); setLehtiMuu(false);
    api.linjanIsometrit(l.id).then((x) => {
      const v: string[] = [];
      for (const i of x.isometrit) {
        if (i.muoto === "vertex_kirja" && i.sivu_alku && i.sivu_loppu) {
          const n = i.sivu_loppu - i.sivu_alku + 1;
          for (let j = 1; j <= n; j++) v.push(`${j}/${n}`);
        } else if (i.lehti_nro) v.push(`${i.lehti_nro}/${i.lehti_kpl ?? "?"}`);
      }
      const yks = [...new Set(v)];
      setLehdet(yks.length ? yks : null);
      if (yks.length === 1) setLehti(yks[0]!);
      const m = new Map<string, Set<string>>();
      for (const i of x.isometrit) for (const o of i.osaluettelo ?? []) {
        if (!o.materiaali) continue;
        const s = m.get(o.materiaali) ?? m.set(o.materiaali, new Set()).get(o.materiaali)!;
        if (o.mitat) s.add(o.mitat);
      }
      setMateriaalit([...m].map(([arvo, mm]) => ({ arvo, mitat: [...mm] })).sort((a, b) => a.arvo.localeCompare(b.arvo)));
      // 8.10.2026: yksi materiaali osaluettelossa täyttyy itse kuten yksi lehti — edellisen istunnon arvo (esim. 316L
      // toiselta linjalta) ei saa jäädä voimaan linjalle, jonka osaluettelo sanoo muuta. Useasta valitaan listasta.
      if (m.size === 1) setMateriaali([...m.keys()][0]!);
    }).catch(() => { setLehdet(null); setMateriaalit([]); });
    // DN kolmesta lähteestä järjestyksessä: linjan oma nimellissuure,
    // isometrin mitasta johdettu, ja viimeisenä käyttäjän valinta
    // listasta. Näppäiltävää vaihtoehtoa ei ole.
    setDn(l.dn_nimellinen ? String(l.dn_nimellinen)
        : l.dn_johdettu ? String(l.dn_johdettu) : "");
    // Ilman tätä valinta tapahtuu ruudun ulkopuolella eikä käyttäjä
    // näe että jotain tapahtui.
    requestAnimationFrame(() =>
      lomake.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  }, []);

  /** Puutteesta suoraan linjalle. */
  async function avaaLinja(id: number, tunnus: string) {
    try {
      // Haku tunnuksella eikä koko listalla: linjoja voi olla satoja,
      // ja oletusraja katkaisisi listan hiljaa.
      const osumat = await api.linjat(tunnus);
      const l = osumat.find((x) => x.id === id) ?? osumat[0];
      if (l) valitseLinja(l);
    } catch { /* lomakkeen voi täyttää käsin */ }
  }

  /** Linjan valinta yhdellä painalluksella viimeisimmistä. */
  function jatkaLinjalta(v: HitsauslokiTilanne["viimeiset"][number]) {
    valitseLinja({
      id: v.id, kohde_id: 0, tunnus: v.tunnus, alue: null, media: null,
      dn_nimellinen: v.dn_nimellinen, putkiluokka: null, ped_luokka: null,
      isometri_olemassa: v.isometri_olemassa, suunniteltuja_hitseja: null,
      tila: "kesken", hitseja: v.hitseja, viim_lehti: v.lehti,
    });
  }

  return (
    <>
      {/* Jaetun linkin virhe: eri syy ja eri lukija kuin lomakkeen
          omilla virheilla. Sama saanto kuin Selailussa. */}
      {esivalintaVirhe && (
        <p className="huom huom--vika" role="alert">{esivalintaVirhe}</p>
      )}
    {tilanne && (
      <>
        {/* Kolme kysymystä ennen kuin linjaa on valittu: mitä tänään
            on tehty, mihin jäin, ja mikä on jumissa. */}
        <section className="panel tilanne">
          <div className="tilanne__luku">
            <b>{tilanne.tanaan.hitseja}</b>
            <span>{tilanne.laaja === false ? t("hl.omia_hitseja_tanaan") : t("hl.hitsia_tanaan")}</span>
          </div>
          <div className="tilanne__luku">
            <b>{tilanne.tanaan.dia_inch}</b>
            <span>{tilanne.laaja === false ? t("hl.omia_tuumia") : t("hl.halkaisijatuumaa")}</span>
          </div>
          {/* T656: kohteen avoimien puutteiden määrä (ei listan pituus, joka on enintään 8); luku avaa Puutteet-listan samalla rajauksella. */}
          {(tilanne.puutteet_yhteensa ?? tilanne.puutteet.length) > 0 && (() => {
            const n = tilanne.puutteet_yhteensa ?? tilanne.puutteet.length;
            const osoite = `#/puutteet?${kohdeId !== "" ? `kohde_id=${kohdeId}&` : ""}tila=avoimet`;
            return (
              <div className="tilanne__luku tilanne__luku--huomio" data-hl-puutteet-auki={n}>
                {tilanne.puutteet_lista
                  ? <a href={osoite} className="hub__linkki" data-hl-puutteet-linkki="" style={{ color: "inherit" }}><b>{n}</b> ›</a>
                  : <b>{n}</b>}
                <span>{t("hl.puutetta_auki", { n })}</span>
              </div>
            );
          })()}
        </section>

        {tilanne.viimeiset.length > 0 && (
          <section className="panel">
            <div className="panel__head">
              <h2>{t("hl.jatka_mista_jait")}</h2>
              <span className="hint">{t("hl.omat_viimeisimmat")}</span>
            </div>
            <div className="pikavalinnat">
              {tilanne.viimeiset.map((v) => (
                <button key={v.id} className="pika" onClick={() => jatkaLinjalta(v)}>
                  <span className="pika__tunnus">{v.tunnus}</span>
                  <span className="pika__meta">
                    {v.lehti !== "-" && `${t("hl.lehti_pieni")} ${v.lehti} · `}
                    {t("hl.n_hitsia", { n: v.hitseja })}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* 25.9.2026: tyhjänä lohko sanoo sen eikä katoa. 207: peruskenttätilille lohkoa ei ole lainkaan (työnjohdon sisältö). */}
        {tilanne.laaja !== false && tilanne.puutteet.length === 0 && (
          <p className="dim" data-toimenpide="jumissa" data-tyhja-tila="">{t("hl.jumissa_tyhja")}</p>
        )}
        {tilanne.puutteet.length > 0 && (
          <section className="paatokset" data-toimenpide="jumissa">
            <div className="panel__head">
              <h2>{t("hl.jumissa")}</h2>
              <span className="hint">{t("hl.jumissa_jarjestys")}</span>
            </div>
            <ul>
              {/* Sama merkintä kuin kapasiteetin päätöslistalla:
                  pysäyttävä on ristiriita, muu on vaje. */}
              {tilanne.puutteet.map((p) => (
                <li key={p.id}
                    className={`pk pk--${p.vaikutus === "pysayttaa" ? "ristiriita" : "vaje"}`}>
                  <span className="pk__merkki" aria-hidden="true" />
                  <div>
                    <div className="pk__otsikko">{puuteKuvausTeksti(p.kuvaus)}</div>
                    <div className="pk__teksti">
                      {p.vastuu ? nayttonimi("puute_vastuu", p.vastuu) : t("hl.vastuu_avoin")}
                      {p.vastuu_ulkoinen && ` · ${t("hl.ulkoinen")}`}
                      {/* "HAVAITTU N VRK SITTEN", EI "ODOTTANUT N VRK".
                          `ika_vrk` on `current_date - havaittu`, eli aika
                          siitä kun HAVAINNOINTI näki asian — ei siitä kun
                          ilmiö syntyi. Havainto ei voi edeltää ilmiötä,
                          joten luku on aina ALARAJA, ei mittaus, ja se
                          esiintyi tarkkana lukuna.

                          Mitattu 2.9.2026: demossa 2 260 avointa puutetta
                          ja kolme eri ikäarvoa (0 · 1 · 7), koska
                          havainnointi on ajanut kolmesti. Pekilossa
                          havainnointi käynnistyi 18.8., joten kaikki
                          4 990 puutetta raportoivat samaa ikää
                          riippumatta siitä onko aukko heinäkuulta vai
                          eiliseltä — luku ei erottele.

                          ⚠ Ja virheen etumerkki on systemaattinen: luku
                          on aina liian pieni. Heinäkuinen puute joka
                          lukee "7 vrk" on esimerkki juuri siitä
                          ongelmasta jonka tuote ratkaisee, esitettynä
                          pienempänä kuin se on.

                          Todellinen ikä vaatii lähteen (suunnitelman
                          päiväys, isometrin revisio, walkdown) — se on
                          mittaus eikä kenttä, ja se on tiekartalla.
                          Tässä vaihdetaan vain sana, koska sana on
                          tosi ja luku ei muutu. */}
                      {" · "}{p.ika_vrk === 0 ? t("hl.havaittu_tanaan") : t("hl.havaittu_sitten", { n: p.ika_vrk })}
                    </div>
                  </div>
                  {/* Linjalle pääsee suoraan puutteesta. Korjaaminen
                      vaatii hitsin muokkauksen, jota ei vielä ole —
                      tämä vie siihen asti oikeaan paikkaan. */}
                  {p.linja_id !== null && (
                    <button className="pk__toiminto"
                            onClick={() => void avaaLinja(p.linja_id!, p.linja_tunnus ?? "")}>
                      {t("hl.avaa_linja")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </>
    )}

    <section className={"panel lomake" + (linja ? " lomake--valittu" : "")} ref={lomake}>
      <div className="panel__head">
        <h2>{linja ? linja.tunnus : t("hl.kenelle_ja_mihin")}</h2>
        <span className="hint">{t("hl.asetetaan_kerran")}</span>
      </div>

      {/* Ensimmäinen kysymys, ei viimeinen: tyyppi ratkaisee mitkä
          numerot ovat käytettävissä. */}
      <div className="tyyppivalinta">
        {HITSITYYPIT.map((t) => (
          <button key={t.arvo}
                  className={"tyyppi" + (tyyppi === t.arvo ? " tyyppi--on" : "")}
                  onClick={() => setTyyppi(t.arvo)}>
            <span className="tyyppi__kirjain">{t.arvo}</span>
            <span className="tyyppi__nimi">{lajinNimi(t.arvo, t.nimi)}</span>
          </button>
        ))}
      </div>

      <div className="lomake__kentat">
        {/* 59: sama valitsin kuin Laiteasennuksessa (KohdeValinta), ei kaksi kopiota. */}
        {!ylapalkissa && <KohdeValinta arvo={kohdeId} muuta={(id) => { setKohdeId(id); setLinja(null); }}
                      kohteet={kohteet.map((k) => ({ id: k.id, tunnus: k.tunnus, nimi: k.nimi }))}
                      vihje={tyomaaVihje && <span className="dim" data-tyomaa="allokaatiosta" style={{ display: "block" }}>{tyomaaVihje}</span>} />}
        <label className="kasvaa">
          {t("hl.linja")}
          <input value={haku} placeholder={esim("100-0254")} autoComplete="off"
                 onChange={(e) => { setHaku(e.target.value); setLinja(null); }} />
        </label>
      </div>

      {!linja && linjat.length === 0 && haku.length >= 2 && (
        <p className="ikkuna__vihje" style={{ padding: "0 12px 12px" }}>
          {t("hl.ei_osumia")} <b>{haku}</b>
          {kohdeId !== "" && ` ${t("hl.valitusta_kohteesta")}`}. {t("hl.tarkista_kirjoitusasu")}
        </p>
      )}
      {!linja && haku.length < 2 && (
        <p className="ikkuna__vihje" style={{ padding: "0 12px 12px" }}>
          {t("hl.kirjoita_linjan_alku")} <span className="mono">100</span>.
        </p>
      )}

      {linjat.length > 0 && !linja && (
        <div className="pikavalinnat">
          {linjat.slice(0, 24).map((l) => (
            <button key={l.id} className="pika" onClick={() => valitseLinja(l)}>
              <span className="pika__tunnus">{l.tunnus}</span>
              <span className="pika__meta">
                {l.dn_nimellinen && `DN ${l.dn_nimellinen} · `}
                {t("hl.n_hitsia", { n: l.hitseja })}
                {!l.isometri_olemassa && ` · ${t("hl.ei_isometria")}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {linja && (
        <>
          {/* 8.10.2026 (Coworkin videokuvaus): kenttätilin hitsaaja on lukittu omaan koodiin, ja henkilölista rajataan
              allokaatioilla — käsin sidottu kenttätili ei näe listassa ketään, ei edes itseään. Varoitus oli silloin väärä:
              hitsaaja on valittu. */}
          {hitsaajat.length === 0 && !(lukitseHitsaaja && juuri) && (
            <p className="huom huom--vika">
              {t("hl.ei_hitsaajia")}
            </p>
          )}

          <div className="lomake__kentat">
            <label>
              {t("hl.lehti")}{linja.isometri_olemassa && " *"}
              {lehdet && !lehtiMuu ? (
                <select value={lehdet.includes(lehti) ? lehti : ""} data-kentta="lehti-valinta" required
                        onChange={(e) => { if (e.target.value === "__muu") { setLehtiMuu(true); setLehti(""); } else setLehti(e.target.value); }}>
                  {lehdet.length > 1 && <option value="">{t("hl.valitse_viivat")}</option>}
                  {lehdet.map((x) => <option key={x} value={x}>{x}</option>)}
                  <option value="__muu">{t("hl.muu_kirjoita")}</option>
                </select>
              ) : (
              <input value={lehti} size={8} data-kentta="lehti"
                     placeholder={linja.isometri_olemassa ? esim("2/4") : t("hl.ei_isometria")}
                     disabled={!linja.isometri_olemassa}
                     required={linja.isometri_olemassa}
                     onChange={(e) => setLehti(e.target.value)} />
              )}
            </label>
            <label>
              DN
              {/*
                * EI näppäiltävä kenttä.
                *
                * Kentällä 18.8.2026 tähän kirjoitettiin 101,6 — ulkohalkaisija
                * millimetreinä, koska se on ainoa luku joka ruudulla näkyy
                * (linjan tunnuksessa ja isometrin mitassa). DN on 100.
                * Hitsaaja ei arvannut väärin; hänelle ei annettu oikeaa
                * vaihtoehtoa.
                *
                * Eikä virhe olisi jäänyt kosmeettiseksi: f_hitsausnormi(dn,
                * prosessi) hakee normin DN:n perusteella, joten väärä DN
                * olisi levinnyt etenemään ja johdettuihin tunteihin. 400 oli
                * onnekas lopputulos.
                */}
              <select value={dn} onChange={(e) => setDn(e.target.value)}>
                <option value="">—</option>
                {dnKoot.map((k) => <option key={k} value={String(k)}>{k}</option>)}
              </select>
              {/* Mistä luku tuli. Johdettu arvo ilman alkuperää on taikaa,
                  ja taikaa ei uskalla korjata kun se on väärin. */}
              {linja.dn_nimellinen == null && linja.dn_johdettu != null
                && String(linja.dn_johdettu) === dn && (
                <span className="dim" >
                  {t("hl.johdettu_isometrista", { mitta: linja.dn_johdettu_mitta ?? "" })}
                </span>
              )}
            </label>
            <label>
              {t("hl.hitsaaja")}
              <select value={juuri} disabled={lukitseHitsaaja} data-kentta="hitsaaja"
                      onChange={(e) => setJuuri(e.target.value)}>
                <option value="">—</option>
                {/* Lukittu oma koodi näkyy aina, vaikka se puuttuisi hitsaajalistalta (osaaminen eri nimellä): ennen kenttä
                    näytti «—» ja lähetti silti koodin. */}
                {lukitseHitsaaja && juuri && !hitsaajat.some((h) => h.lyhenne === juuri) && <option value={juuri}>{juuri} · {t("hl.mina")}</option>}
                {hitsaajat.map((h) => (
                  <option key={h.id} value={h.lyhenne ?? ""}>{h.lyhenne} · {h.nimi}</option>
                ))}
              </select>
            </label>
            {lukitseHitsaaja && juuri && hitsaajat.length > 0 && !hitsaajat.some((h) => h.lyhenne === juuri) && (
              <p className="huom huom--vika" data-hitsaaja-ei-listalla={juuri} style={{ flexBasis: "100%" }}>
                {t("hl.koodi_ei_listalla", { koodi: juuri })}
              </p>
            )}
            <label>
              {t("hl.asentaja")}
              <select value={taytto} onChange={(e) => setTaytto(e.target.value)}>
                <option value="">—</option>
                {asentajat.map((h) => (
                  <option key={h.id} value={h.lyhenne ?? ""}>{h.lyhenne} · {h.nimi}</option>
                ))}
              </select>
            </label>
            <label>
              {t("hl.prosessi")}
              <input value={prosessi} size={6} onChange={(e) => setProsessi(e.target.value)} />
            </label>
            <label>
              WPS
              <select value={wpsId}
                      onChange={(e) => setWpsId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">—</option>
                {wpsLista.map((w) => <option key={w.id} value={w.id}>{w.tunnus}</option>)}
              </select>
            </label>
            <label>
              {t("hl.materiaali")} *
              <input value={materiaali} size={10} placeholder={esim("316L")} required list="hl-materiaalit" data-kentta="materiaali"
                     onChange={(e) => setMateriaali(e.target.value)} />
              {/* 7.10.2026: ehdotukset linjan osaluettelosta (materiaali, mitat selitteenä); vapaa teksti säilyy. */}
              <datalist id="hl-materiaalit">{materiaalit.map((m) => <option key={m.arvo} value={m.arvo}>{m.mitat.join(", ")}</option>)}</datalist>
            </label>
            <label className="kasvaa">
              {t("hl.lisaaine")}
              <input value={lisaaine} onChange={(e) => setLisaaine(e.target.value)} />
            </label>
          </div>

          {materiaaliPuuttuu && (
            <p className="huom huom--vika">
              {t("hl.materiaali_pakollinen")}
            </p>
          )}

          {lehtiPuuttuu && (
            <p className="huom huom--vika">
              {t("hl.lehti_pakollinen")}
            </p>
          )}

          {!linja.isometri_olemassa && (
            <p className="ikkuna__vihje">
              {t("hl.ei_isometria_selite")}
            </p>
          )}

          <div className="lomake__toiminnot" style={{ padding: "0 12px 14px" }}>
            <button className="nappi nappi--paa nappi--iso" onClick={aloita}
                    disabled={lehtiPuuttuu || materiaaliPuuttuu}>
              {t("hl.aloita_kirjaus")}
            </button>
          </div>
        </>
      )}
    </section>
    </>
  );
}

/* ── Kirjaus ────────────────────────────────────────────── */

/** Osan nimike: valinta listalta tai "Muu" → vapaa teksti. */
function NimikeValinta({ arvo, aseta }: { arvo: string; aseta: (v: string) => void }) {
  const listalla = NIMIKKEET.includes(arvo);
  const [muu, setMuu] = useState(!listalla && arvo !== "");
  return (
    <>
      <select value={muu ? "__muu" : (listalla ? arvo : "")}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__muu") { setMuu(true); aseta(""); }
                else { setMuu(false); aseta(v); }
              }}>
        <option value="">—</option>
        {NIMIKKEET.map((n) => <option key={n} value={n}>{nimikeTeksti(n)}</option>)}
        <option value="__muu">{t("hl.muu")}</option>
      </select>
      {muu && (
        <input value={arvo} placeholder={t("hl.nimike_pieni")} autoComplete="off"
               onChange={(e) => aseta(e.target.value)} />
      )}
    </>
  );
}

function Kirjaus({ istunto, tarraEtuliite, linjaKadonnut, kayttajaId, eiTehda }: { istunto: Istunto; tarraEtuliite: string; linjaKadonnut: () => void; kayttajaId: number; eiTehda: () => void }) {
  /* T426 (pääChatin huomio Tonin välittämänä 7.10.2026): hitsi ei saa mennä eri kohteeseen kuin näkyvissä oleva. Istunto
     elää selaimessa linjan mukana, ja yläpalkin kohteen voi vaihtaa kesken istunnon — silloin kirjaus estyy ja syy sanotaan.
     Istunnon kohde luetaan linjasta (tallennettu istunto ei kanna kohdetta). */
  const kohdeKtx = useKohde();
  const [istunnonKohde, setIstunnonKohde] = useState<number | null>(null);
  useEffect(() => {
    api.linjaTunnuksella(istunto.linja_tunnus)
      .then((r) => setIstunnonKohde(r.find((x) => x.id === istunto.linja_id)?.kohde_id ?? null))
      .catch(() => setIstunnonKohde(null));
  }, [istunto.linja_id, istunto.linja_tunnus]);
  const istunnonKohdeTunnus = kohdeKtx.kohteet.find((x) => x.id === istunnonKohde)?.tunnus ?? null;
  const valittuTunnus = kohdeKtx.kohteet.find((x) => x.id === kohdeKtx.valittu)?.tunnus ?? null;
  const kohdeRistiriita = kohdeKtx.kaytossa && kohdeKtx.valittu != null && istunnonKohde != null && istunnonKohde !== kohdeKtx.valittu;
  const [numero, setNumero] = useState("");
  // 211: valitun numeron vaiheet seuraava-vastauksesta (johdetaan alla kun numerot on haettu).
  /* 153/155: alinumero on punakynä, ja kentän perustelu on pakollinen.
     ⛔ Palvelin torjuu ilman sitä — ja ilman tätä kenttää työ PYSÄHTYISI
     siihen, koska kenttätekijä ei voisi antaa perustelua mistään.
     Kenttä ilmestyy vasta kun numerossa on alinumero: se ei ole
     jokapäiväinen tapaus eikä saa olla jokapäiväinen lomakekenttä. */
  const [punakynaPeruste, setPunakynaPeruste] = useState("");
  const alinumero = /^[0-9]+[.,][0-9]+/.test(numero.trim());
  /* Ehdotus haetaan PALVELIMELTA samasta reitistä kuin Selailun «+ hitsi».
     Kaksi laskentaa samasta asiasta erkanee, ja selaimen kopio olisi se
     joka on väärässä hiljaa. */
  const [alinumeroEhdotus, setAlinumeroEhdotus] = useState<string | null>(null);
  useEffect(() => {
    if (!alinumero) { setAlinumeroEhdotus(null); return; }
    const kanta = numero.trim().split(/[.,]/)[0] ?? "";
    if (!kanta) return;
    let voimassa = true;
    api.seuraavaAlinumero(istunto.linja_id, kanta, istunto.lehti || undefined)
      .then((r) => { if (voimassa) setAlinumeroEhdotus(r.ehdotus); })
      .catch(() => { if (voimassa) setAlinumeroEhdotus(null); });
    return () => { voimassa = false; };
  }, [alinumero, numero, istunto.linja_id, istunto.lehti]);
  const [tarra, setTarra] = useState("");
  const [numerot, setNumerot] = useState<SeuraavaNumero | null>(null);
  const valitunVaiheet = numerot?.varatut.find((v) => v.nro === numero.trim())?.vaiheet ?? [];
  const asennettu = valitunVaiheet.find((v) => v.vaihe === "asennus") ?? null;
  const esivalmistettu = asennettu ? null : (valitunVaiheet.find((v) => v.vaihe === "esivalmiste") ?? null);
  // T675 (Toni 31.10.2026 klo 11.00 kohta 5): valitun numeron hitsin huomio kenttänäkymään (liitostieto erotettu).
  const valitunHuomio = hitsinHuomio(numerot?.varatut.find((v) => v.nro === numero.trim())?.huomiot);
  const [alkuMuisti] = useState(() => lueSulatusmuisti());
  const [sulatukset, setSulatukset] = useState<[string, string]>(() => alkuMuisti?.sulatukset ?? ["", ""]);
  const [nimikkeet, setNimikkeetSuoraan] = useState<[string, string]>(() => alkuMuisti?.nimikkeet ?? ["", ""]);
  // Kysytään kun asiayhteys vaihtui; kirjaus odottaa vastausta (Kyllä / Vaihda).
  const [sulatusKysymys, setSulatusKysymys] = useState<SulatusMuutos | null>(
    () => sulatusmuistinMuutos(alkuMuisti, kayttajaId, istunto.linja_id, istunto.lehti, Date.now()));
  /** Osan nimike vaihtuu → sen osan sulatus tyhjenee: eri putki tai eri osa on eri sulatus. */
  const setNimikkeet = (uusi: [string, string]) => {
    setSulatukset((s) => [uusi[0] !== nimikkeet[0] ? "" : s[0], uusi[1] !== nimikkeet[1] ? "" : s[1]]);
    setNimikkeetSuoraan(uusi);
  };
  // Materiaali tulee istunnosta ja vaihtuu sulatuksen mukana:
  // uusi putki tarkoittaa usein myös uutta ainetta.
  const [materiaali, setMateriaali] = useState(istunto.materiaali);
  const [vaihdaSulatus, setVaihdaSulatus] = useState(false);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  /*
   * Virhe kantaa kentän mukanaan.
   *
   * Palvelin on kertonut kentän koko ajan (`kentta`: dn, tarra,
   * piirustuslehti, osat.0.sulatus…), ja selain heitti sen pois ja näytti
   * yhden punaisen rivin napin alla. Kentällä se tarkoittaa että sama
   * arvo yritetään uudelleen: 18.8. kolme yritystä minuutissa, kaikki
   * 400, eikä ruudulla lukenut mikä oli väärin.
   */
  const [virhe, setVirhe] = useState<{ viesti: string; kentta?: string } | null>(null);

  /** Virheteksti tälle kentälle, tai null. */
  const kenttaVirhe = (nimi: string) =>
    virhe?.kentta === nimi ? virhe.viesti : null;

  /**
   * Istunnon kentät eivät ole tässä lomakkeessa: DN, lehti ja materiaali
   * asetetaan istuntoa aloitettaessa. Niiden virhe näytetään yleisenä ja
   * kerrotaan mistä se korjataan — muuten käyttäjä etsii kenttää jota ei
   * ole näkyvissä.
   */
  const ISTUNNON_KENTAT = ["dn", "piirustuslehti", "materiaali"];
  const [viimeiset, setViimeiset] = useState<Hitsi[]>([]);
  // Korjattava hitsi viimeksi kirjatuista (T309): hitsaaja korjaa omat ja parinsa 24 h kirjauksesta.
  const [korjattava, setKorjattava] = useState<number | null>(null);
  const [kuvat, setKuvat] = useState<number | null>(null);
  const nappi = useRef<HTMLButtonElement>(null);
  // T323: sama tunniste kunnes hitsi menee perille — uusi painallus katkenneen vastauksen jälkeen ei tee toista.
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();

  /* ⛔ Ehdotus ei ylikirjoita kirjoitettua numeroa (24.9.2026). Avauksen haku
     kirjoitti ehdotuksen kenttään ehdoitta: jos hitsaaja ehti kirjoittaa 6.1
     ennen vastausta, numero vaihtui hänen ohitseen — hitaalla työmaayhteydellä
     todellinen tilanne, ja kävelyn hitsikirjausväite osui juuri siihen.
     Kirjauksen jälkeen `pakota` siirtää kentän seuraavaan, koska silloin
     kentässä on juuri tallennettu numero eikä käyttäjän keskeneräinen syöte. */
  const haeSeuraava = useCallback(async (pakota = false) => {
    try {
      const r = await api.seuraavaHitsinumero(istunto.linja_id, istunto.lehti);
      setNumero((n) => (pakota || n.trim() === "" ? r.seuraava : n));
      setNumerot(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) linjaKadonnut();
      /* muu virhe: jätetään käsin syötettäväksi */
    }
  }, [istunto.linja_id, istunto.lehti, linjaKadonnut]);

  const haeViimeiset = useCallback(async () => {
    try { setViimeiset(await api.hitsit(istunto.linja_id, 8)); }
    catch { setViimeiset([]); }
  }, [istunto.linja_id]);

  useEffect(() => { void haeSeuraava(); void haeViimeiset(); }, [haeSeuraava, haeViimeiset]);
  // Muisti kirjoitetaan vasta kun kysymykseen on vastattu: muuten lataus kirjoittaisi uuden asiayhteyden ja seuraava
  // sivun lataus ohittaisi kysymyksen.
  useEffect(() => {
    if (sulatusKysymys) return;
    tallenna(SULATUSMUISTI_AVAIN, { kayttaja_id: kayttajaId, linja_id: istunto.linja_id, lehti: istunto.lehti,
                                    nimikkeet, sulatukset, hetki: Date.now() } satisfies Sulatusmuisti);
  }, [sulatukset, nimikkeet, sulatusKysymys, kayttajaId, istunto.linja_id, istunto.lehti]);

  const puuttuuSulatus = !sulatukset[0].trim() || !sulatukset[1].trim();

  async function kirjaa() {
    if (kohdeRistiriita || sulatusKysymys) return;   // T426 ja sulatuskysymys: nappi on estetty; Enter-näppäin ei ohita sitä
    if (!numero.trim()) { setVirhe({ viesti: t("hl.anna_numero"), kentta: "hitsinumero" }); return; }
    // T565 (Toni 13.10.2026): kenttäkirjauksessa tarraa ei normalisoida vaan validoidaan — etuliite valmiina, tasan 4 numeroa.
    if (tarraEtuliite && tarra.trim() && !/^\d{4}$/.test(tarra.trim())) { setVirhe({ viesti: t("hl.tarra_muoto"), kentta: "tarra" }); return; }
    setTallennetaan(true);
    setVirhe(null);
    try {
      const r = await api.kirjaaHitsi({
        kirjaustunniste: tunniste,
        linja_id: istunto.linja_id,
        piirustuslehti: istunto.lehti,
        hitsinumero: numero.trim(),
        ...(alinumero && punakynaPeruste.trim()
              ? { punakyna_peruste: punakynaPeruste.trim() } : {}),
        // 211 (Toni 8.10.2026): jo esivalmistettu numero kirjataan asennuksena — sama sauma, toinen vaihe.
        hitsityyppi: esivalmistettu && istunto.tyyppi !== "R" ? "A" : istunto.tyyppi,
        hitsausprosessi: istunto.prosessi || undefined,
        wps_id: istunto.wps_id,
        juuri_hitsaaja_koodi: istunto.juuri || undefined,
        // Asentaja on hitsaajan pari (T331, Toni 14.9.2026): asentajan kenttään, ei täyttöhitsaajaksi.
        asentaja_koodi: istunto.taytto || undefined,
        lisaaine: istunto.lisaaine || undefined,
        paiva: istunto.paiva,
        ...(istunto.dn && { dn: istunto.dn }),
        // Tallennettava arvo on koko tarra; näppäilty on vain loppuosa.
        ...(tarra.trim() && { tarra: tarraEtuliite + tarra.trim() }),
        // Kaksi osaa: hitsi liittää vähintään kaksi, ja molempien
        // sulatus on jäljitettävyyden ydin.
        osat: [
          { osalaji: nimikkeet[0] || undefined, materiaali: materiaali || undefined, sulatus: sulatukset[0] || undefined },
          { osalaji: nimikkeet[1] || undefined, materiaali: materiaali || undefined, sulatus: sulatukset[1] || undefined },
        ],
      });

      // Huomiot yhdelle riville. Sulatuspuute on merkintä, pätevyys-
      // varoitus on ⚠: sauma on kirjattu, mutta se saattaa olla tehty
      // vanhentuneella pätevyydellä — juuri se joka on dokumentoitava.
      const huomiot: string[] = [];
      if (puuttuuSulatus) huomiot.push(t("hl.sulatus_puuttuu_merkitty"));
      if (r.varoitukset?.length) huomiot.push(...r.varoitukset.map((v) => `⚠ ${v}`));
      const teko = r.toisto ? t("hl.teko.toisto")
        // 211: toinen vaihe samalle saumalle nimetään, ettei se näytä uudelta hitsiltä.
        : r.vaihe_lisatty ? t("hl.vaihe.lisatty", { vaihe: t(`hl.vaihe.${r.vaihe_lisatty}`) })
        : r.taydennetty ? t("hl.teko.taydennetty") : t("hl.teko.kirjattu");
      setIlmoitus(
        huomiot.length
          ? `${t("hl.hitsi")} ${numero} ${teko} — ${huomiot.join("; ")}.`
          : `${t("hl.hitsi")} ${numero} ${teko}.`
      );
      vaihdaTunniste();   // seuraava hitsi on uusi kirjaus
      setVaihdaSulatus(false);
      // Tauko lasketaan edellisestä kirjauksesta, ei sulatuksen muutoksesta.
      tallenna(SULATUSMUISTI_AVAIN, { kayttaja_id: kayttajaId, linja_id: istunto.linja_id, lehti: istunto.lehti,
                                      nimikkeet, sulatukset, hetki: Date.now() } satisfies Sulatusmuisti);
      setTarra("");   // jokaisella hitsillä oma tarra
      // Perustelu koskee tätä yhtä lisäystä, ei seuraavaa: jäänyt teksti
      // menisi seuraavan hitsin perusteluksi väärin.
      setPunakynaPeruste("");
      await Promise.all([haeSeuraava(true), haeViimeiset()]);
      // Kohdistus takaisin nappiin: seuraava hitsi on yksi painallus.
      nappi.current?.focus();
    } catch (e) {
      setVirhe(e instanceof ApiError
        ? { viesti: virheTeksti(e.virhe, e.message), ...(e.kentta ? { kentta: e.kentta } : {}) }
        : { viesti: t("hl.kirjaus_ei_onnistunut") });
    } finally { setTallennetaan(false); }
  }

  return (
    <>
      {/* Istunnon tiedot tiiviisti: mitä ollaan kirjaamassa. */}
      <section className="panel istunto">
        <div className="istunto__rivi">
          <span className={"istunto__tyyppi istunto__tyyppi--" + istunto.tyyppi}>
            {lajinNimi(istunto.tyyppi, nayttonimi("istunto_laji", istunto.tyyppi))}
          </span>
          {istunnonKohdeTunnus && <span className="istunto__osa" data-istunto-kohde={istunnonKohde ?? ""}>{t("hl.kohde_pieni")} {istunnonKohdeTunnus}</span>}
          <span className="istunto__osa"><b>{istunto.linja_tunnus}</b></span>
          {istunto.lehti !== "-" && <span className="istunto__osa">{t("hl.lehti_pieni")} {istunto.lehti}</span>}
          {istunto.dn && <span className="istunto__osa">DN {istunto.dn}</span>}
          <span className="istunto__osa">{istunto.juuri || "—"}
            {istunto.taytto && ` / ${istunto.taytto}`}</span>
          <span className="istunto__osa">{istunto.prosessi}</span>
          <span className="istunto__osa mono">{naytaPvm(istunto.paiva)}</span>
        </div>
      </section>

      {/* Kirjaus: numero ja sulatukset, ei muuta. */}
      <section className="panel kirjaus">
        {/* Ohje lomakkeen yläosaan (Toni 23.9.2026): kenttäkirjaus tehdään
            puhelimella, usein hanskat kädessä ja huonossa valossa, ja alinumero
            on käyttäjälle uusi käsite. Sama komponentti kuin kuudessa näkymässä. */}
        <Ohje
          kuka={<>{t("hl.ohje.kuka")}</>}
          lukujenOtsikko={t("hl.ohje.numerot_otsikko")}
          luvut={<>{t("hl.ohje.numerot")}</>} />
        <div className="kirjaus__numero">
          <label>
            {t("hl.hitsi")}
            <input type="text" inputMode="numeric" value={numero}
                   onChange={(e) => setNumero(e.target.value)} 
                   aria-invalid={!!kenttaVirhe("hitsinumero")} />
            {kenttaVirhe("hitsinumero") && (
              <span className="kenttavirhe">{kenttaVirhe("hitsinumero")}</span>
            )}
            {/* Toni 31.10.2026 klo 16.05: pitkä huomio rivittyy solun sisällä eikä venytä riviä vaakavieritykseen. */}
            {valitunHuomio && <span className="huom" data-kentta-hitsin-huomio={numero.trim()}
              style={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%", minWidth: 0 }}>{t("sel.hitsin_huomio")}: {valitunHuomio}</span>}
            {/* 211: numeron vaiheet — «Esivalmistettu [pvm] / [hitsaaja]» ja asennus tarjolla valmiiksi (Toni 8.10.2026). */}
            {esivalmistettu && istunto.tyyppi !== "R" && (
              <span className="huom" data-hitsi-vaihe="esivalmistettu">
                {t("hl.vaihe.esivalmistettu", { pvm: naytaPvm(esivalmistettu.paiva) ?? "—", koodi: esivalmistettu.koodi ?? "—" })}
              </span>
            )}
            {asennettu && istunto.tyyppi !== "R" && (
              <span className="huom huom--vika" data-hitsi-vaihe="asennettu">
                {t("hl.vaihe.asennettu", { pvm: naytaPvm(asennettu.paiva) ?? "—", koodi: asennettu.koodi ?? "—" })}
              </span>
            )}
          </label>
          {alinumero && (
            <label className="kirjaus__punakyna">
              {t("hl.punakyna.miksi")}
              <input type="text" value={punakynaPeruste}
                     placeholder={t("hl.punakyna.esim")}
                     onChange={(e) => setPunakynaPeruste(e.target.value)}
                     aria-invalid={!!kenttaVirhe("punakyna_peruste")} />
              {kenttaVirhe("punakyna_peruste") && (
                <span className="kenttavirhe">{kenttaVirhe("punakyna_peruste")}</span>
              )}
              <span className="vihje">
                {t("hl.punakyna.selite", { numero: numero.trim() })}
              </span>
            </label>
          )}
          {/* Tarrakenttä vain kun tarran etuliite on asetettu (T333, Toni 14.9.2026: «piilotetaan turhana kaikkialta
              muualta paitsi JSG:ltä — muualla hitsaaja kirjoittaa oman hitsaajakoodinsa ja päivän»). Asetus eikä
              instanssin nimi: mitattu 14.9., etuliite on asetettu vain pekilossa. */}
          {tarraEtuliite && (
          <label>
            {t("hl.tarra")}
            {/*
              * Etuliite on vakio eikä sitä näppäillä. Kenttätesti 18.8.: koko
              * tarratunnus kirjoitettiin joka hitsillä hansikkaat kädessä —
              * useita painalluksia ja juuri se hetki jossa kirjoitusvirhe
              * syntyy. Etuliite tulee asetuksesta, koska asiakas ja vuosi
              * vaihtuvat.
              *
              * PLACEHOLDER EI SAA OLLA TOISEN ASIAKKAAN MUOTO. Kun etuliite
              * on asetettu, esimerkki on pelkkä juokseva numero — se on
              * geneerinen. Kun etuliitettä EI ole, emme tiedä tämän asiakkaan
              * tarramuotoa, ja silloin kenttä jätetään tyhjäksi: arvaus
              * näyttäisi ohjeelta. Aiemmin tässä luki erään toisen asiakkaan
              * tarramuoto, eli sama vika kuin 004:n Pro-Tot-oletuksessa —
              * esimerkki kirjoitettiin siitä asiakkaasta joka oli pöydällä.
              */}
            <span className="tarra">
              {tarraEtuliite && <span className="tarra__etuliite">{tarraEtuliite}</span>}
              <input type="text" inputMode={tarraEtuliite ? "numeric" : "text"}
                     value={tarra} placeholder={tarraEtuliite ? esim("6515") : ""}
                     autoCapitalize="characters"
                     maxLength={tarraEtuliite ? 4 : undefined}
                     onChange={(e) => setTarra(tarraEtuliite ? e.target.value.replace(/\D/g, "") : e.target.value)}
                            aria-invalid={!!kenttaVirhe("tarra")} />
            </span>
            {kenttaVirhe("tarra") && (
              <span className="kenttavirhe">{kenttaVirhe("tarra")}</span>
            )}
          </label>
          )}

          {/* Varatut näkyviin: kentällä numeroita kokeillaan kunnes
              jokin menee läpi, jos järjestelmä vain estää kertomatta
              miksi. Tyyppi luetaan sanasta (Asennus / Esivalmiste /
              Korjaus); esivalmiste erottuu lisäksi harmaalla taustalla —
              ei punaisella, koska punainen tarkoittaa tässä vikaa. */}
          {numerot && numerot.varatut.length > 0 && (
            <div className="varatut">
              {numerot.varatut.some((v) => v.suunniteltu) && (
                <>
                  <span className="varatut__otsikko">{t("hl.suunnitellut")}</span>
                  {numerot.varatut.filter((v) => v.suunniteltu).slice(0, 60).map((v) => (
                    <button key={v.nro} type="button" className="varattu varattu--vapaa"
                            title={t("hl.suunniteltu_title")} onClick={() => setNumero(v.nro)}>
                      {v.nro}
                    </button>
                  ))}
                </>
              )}
              {numerot.varatut.some((v) => !v.suunniteltu) && (
                <span className="varatut__otsikko">{t("hl.jo_kirjattu")}</span>
              )}
              {/* Tyypin SANA näkyviin, ei vain kirjain ja title:
                  title näkyy vain hoverissa eli puhelimella ei koskaan,
                  ja pelkkä väri ei kerro mitään mustavalkoisena. */}
              {numerot.varatut.filter((v) => !v.suunniteltu).slice(0, 40).map((v) => (
                <span key={v.nro} className={"varattu varattu--" + v.tyyppi}>
                  {v.nro} · {lajinNimi(v.tyyppi, HITSITYYPIT.find((x) => x.arvo === v.tyyppi)?.nimi)}
                </span>
              ))}
              {/* ⛔ «Vapaana välissä» on KANTANUMEROSARJAN aukkolista (1…N).
                  Alinumeron kanssa se ei liity mihinkään: kun kirjoitettuna
                  on 3.1, lista tarjoaa kokonaislukuja, ja klikkaus korvaisi
                  kirjoitetun alinumeron hiljaa. Mitattu 21.9.2026 — ei ollut
                  tarkoituksellista, vaan lista näytettiin riippumatta siitä
                  mitä kenttään oli kirjoitettu.
                  Alinumerolla näytetään sen sijaan sama tieto samasta
                  lähteestä kuin Selailun «+ hitsi»: palvelimen ehdotus. */}
              {alinumero ? (
                <>
                  <span className="varatut__otsikko">{t("hl.alinumero")}</span>
                  <span className="varattu varattu--vapaa" style={{ cursor: "default" }}>
                    {alinumeroEhdotus
                      ? t("hl.seuraava_vapaa", { nro: alinumeroEhdotus })
                      : t("hl.haetaan_vapaata")}
                  </span>
                </>
              ) : numerot.aukot.length > 0 && (
                <>
                  <span className="varatut__otsikko">{t("hl.vapaana_valissa")}</span>
                  {numerot.aukot.map((n) => (
                    <button key={n} className="varattu varattu--vapaa"
                            onClick={() => setNumero(n)}>{n}</button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {sulatusKysymys && (
          <div className="huom huom--varoitus" role="alert" data-sulatus-kysy={sulatusKysymys} style={{ fontSize: "inherit", margin: "8px 0" }}>
            <b>{t("hl.sulatus_kysy")}</b> {t(`hl.sulatus_kysy.${sulatusKysymys}`, { h: SULATUS_TAUKO_H })}
            <div className="mono" style={{ margin: "4px 0" }}>
              {(nimikkeet[0] ? nimikeTeksti(nimikkeet[0]) : t("hl.osa_n_pieni", { n: 1 }))} · {sulatukset[0] || t("hl.sulatus_puuttuu")}
              {" + "}{(nimikkeet[1] ? nimikeTeksti(nimikkeet[1]) : t("hl.osa_n_pieni", { n: 2 }))} · {sulatukset[1] || t("hl.sulatus_puuttuu")}
            </div>
            <button className="nappi nappi--paa" data-sulatus-kylla="" onClick={() => setSulatusKysymys(null)}>{t("hl.sulatus_kylla")}</button>{" "}
            <button className="nappi nappi--kevyt" data-sulatus-vaihda="" onClick={() => { setSulatukset(["", ""]); setVaihdaSulatus(true); setSulatusKysymys(null); }}>{t("hl.vaihda")}</button>
          </div>
        )}
        <div className="kirjaus__sulatus">
          <div className="kirjaus__otsikko">
            {t("hl.osat_ja_sulatukset")}
            {!vaihdaSulatus && (
              <button className="nappi nappi--kevyt" onClick={() => setVaihdaSulatus(true)}>
                {t("hl.vaihda")}
              </button>
            )}
          </div>

          {vaihdaSulatus ? (
            <div className="osat">
              <div className="osarivi">
                <span className="osarivi__nro">{t("hl.osa_n", { n: 1 })}</span>
                <label>
                  {t("hl.nimike")}
                  <NimikeValinta arvo={nimikkeet[0]} aseta={(v) => setNimikkeet([v, nimikkeet[1]])} />
                </label>
                <label className="kasvaa">
                  {t("hl.sulatus")}
                  <input value={sulatukset[0]} autoComplete="off" placeholder={esim("AB12345")}
                         onChange={(e) => setSulatukset([e.target.value, sulatukset[1]])} 
                                aria-invalid={!!kenttaVirhe("osat.0.sulatus")} />
                         {kenttaVirhe("osat.0.sulatus") && (
                           <span className="kenttavirhe">{kenttaVirhe("osat.0.sulatus")}</span>
                         )}
                </label>
              </div>
              <div className="osarivi">
                <span className="osarivi__nro">{t("hl.osa_n", { n: 2 })}</span>
                <label>
                  {t("hl.nimike")}
                  <NimikeValinta arvo={nimikkeet[1]} aseta={(v) => setNimikkeet([nimikkeet[0], v])} />
                </label>
                <label className="kasvaa">
                  {t("hl.sulatus")}
                  <input value={sulatukset[1]} autoComplete="off" placeholder={esim("AB12345")}
                         onChange={(e) => setSulatukset([sulatukset[0], e.target.value])} 
                                aria-invalid={!!kenttaVirhe("osat.1.sulatus")} />
                         {kenttaVirhe("osat.1.sulatus") && (
                           <span className="kenttavirhe">{kenttaVirhe("osat.1.sulatus")}</span>
                         )}
                </label>
              </div>
              <label className="osat__materiaali">
                {t("hl.materiaali_molemmat")}
                <input value={materiaali} placeholder={t("hl.esim_316l")} autoComplete="off"
                       onChange={(e) => setMateriaali(e.target.value)} />
              </label>
            </div>
          ) : (
            <div className="kirjaus__arvot mono">
              <span className={sulatukset[0] ? "" : "puuttuu"}>
                {nimikkeet[0] ? nimikeTeksti(nimikkeet[0]) : t("hl.osa_n_pieni", { n: 1 })}{sulatukset[0] ? ` · ${sulatukset[0]}` : ` · ${t("hl.sulatus_puuttuu")}`}
              </span>
              <span className="dim">+</span>
              <span className={sulatukset[1] ? "" : "puuttuu"}>
                {nimikkeet[1] ? nimikeTeksti(nimikkeet[1]) : t("hl.osa_n_pieni", { n: 2 })}{sulatukset[1] ? ` · ${sulatukset[1]}` : ` · ${t("hl.sulatus_puuttuu")}`}
              </span>
            </div>
          )}
        </div>

        {kohdeRistiriita && (
          <p className="huom huom--vika" role="alert" data-kohderistiriita="">
            {t("hl.kohderistiriita", { linja: istunto.linja_tunnus, kohde: istunnonKohdeTunnus ?? "?", valittu: valittuTunnus ?? "?" })}
          </p>
        )}
        <button ref={nappi} className="nappi nappi--paa nappi--iso kirjaus__nappi"
                disabled={tallennetaan || kohdeRistiriita || !!sulatusKysymys} data-kirjaa-hitsi="" onClick={() => void kirjaa()}>
          {tallennetaan ? t("hl.kirjataan") : t("hl.kirjaa_hitsi", { numero })}
        </button>
        {/* 158: kentällä ei tarvitse vaihtaa Selailuun. Kirjaus jää ESITYKSEKSI, jonka työnjohto hyväksyy — kentällä ei synny
            hyväksyttyä merkintää vahingossa. T654 (Toni 25.10.2026 klo 20.15): toissijainen nappi «Kirjaa hitsi» -napin alla,
            koska se koskee kirjattavaa saumaa; puhelinkoko 44 px / 16 px. */}
        <button className="nappi nappi--kevyt" data-hl-ei-tehda="" style={{ ...KENTTANAPPI, marginTop: 8 }}
                title={t("hl.ei_tehda.selite")} onClick={eiTehda}>
          {t("hl.ei_tehda")}
        </button>

        {/* Yleinen banneri vain silloin kun virhe ei osu näkyvissä olevaan
            kenttään. Istunnon kentät (DN, lehti, materiaali) ovat toisessa
            näkymässä, joten niistä kerrotaan mistä ne korjataan. */}
        {virhe && (!virhe.kentta || ISTUNNON_KENTAT.includes(virhe.kentta)) && (
          <p className="huom huom--vika" role="alert">
            {virhe.viesti}
            {virhe.kentta && ISTUNNON_KENTAT.includes(virhe.kentta)
              && ` — ${t("hl.korjaa_istunnosta")}`}
          </p>
        )}
        {ilmoitus && !virhe && <p className="huom" role="status">{ilmoitus}</p>}

        <p className="ikkuna__vihje">
          {t("hl.sulatukset_periytyvat")}
        </p>
      </section>

      {viimeiset.length > 0 && (
        <section className="panel scroll kirjaus__viimeiset" data-viimeiset="">
          <div className="panel__head">
            <h2>{t("hl.viimeksi_kirjatut")}</h2>
            <span className="hint">{t("hl.n_viimeisinta", { n: viimeiset.length })}</span>
          </div>
          <table className="tbl tbl--hitsit">
            <thead>
              <tr>
                <th className="th--nimi">{t("hl.hitsi")}</th>
                <th className="th--vasen">{t("hl.sulatukset")}</th>
                <th>{t("hl.hitsaaja")}</th>
                <th>{t("hl.paiva")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {viimeiset.map((h) => (
                <Fragment key={h.id}>
                <tr>
                  <th className="th--nimi">
                    <span className="lyh">
                      <HitsiTunnus numero={h.hitsinumero} lehti={h.piirustuslehti} />
                    </span>
                    {h.hitsityyppi && h.hitsityyppi !== "A" && (
                      <span className={"istunto__tyyppi istunto__tyyppi--" + h.hitsityyppi}
                        title={lajinSelite(h.hitsityyppi)} data-lajimerkki={h.hitsityyppi}>
                        {h.hitsityyppi}
                      </span>
                    )}
                  </th>
                  <td className="td--vasen mono">
                    {h.osat.map((o) => o.sulatus ?? "—").join(" + ")}
                  </td>
                  <td className="mono">{h.juuri_hitsaaja_koodi ?? "—"}</td>
                  <td className="mono">{naytaPvm(h.paiva) ?? <span className="dim">—</span>}</td>
                  <td>
                    {/* 7.10.2026 (Norjan erä): kuva kirjattuun hitsiin puhelimen kameralla tai galleriasta. */}
                    {(h.kuva_lisattavissa || (h.kuvia ?? 0) > 0) && (
                      <button className="nappi nappi--kevyt" data-hitsi-kuva={h.id}
                              onClick={() => setKuvat(kuvat === h.id ? null : h.id)}>
                        {kuvat === h.id ? t("hl.sulje") : (h.kuvia ?? 0) > 0 ? t("hl.kuvat_n", { n: h.kuvia ?? 0 }) : t("hl.lisaa_kuva")}
                      </button>
                    )}
                    {/* Palvelin päättää (korjattavissa): oma tai parin hitsi, 24 h kirjauksesta. */}
                    {h.korjattavissa && (
                      <button className="nappi nappi--kevyt" data-korjaa={h.id}
                              onClick={() => setKorjattava(korjattava === h.id ? null : h.id)}>
                        {korjattava === h.id ? t("hl.sulje") : t("hl.korjaa")}
                      </button>
                    )}
                  </td>
                </tr>
                {/* ⛔ Lohko aukeaa TÄMÄN rivin alle (Toni 21.9.2026). Ennen se
                    piirtyi taulukon jälkeen, jolloin puhelimella korjattava
                    rivi jäi ruudun ulkopuolelle ja lomake näytti kuuluvan
                    johonkin muuhun hitsiin.
                    `key` on `h.id`: ilman sitä React pitää saman komponentin
                    rivistä toiseen, eivätkä esitäytetyt kentät päivity —
                    lomake näyttäisi EDELLISEN hitsin arvot. */}
                {kuvat === h.id && (
                  <tr className="tbl__laajennus" data-hitsi-kuvat-auki={h.id}>
                    <td colSpan={5}>
                      <Liitteet kohdeTaulu="hitsi" kohdeId={h.id} saaMuokata={!!h.kuva_lisattavissa} saaPoistaa={false}
                                otsikko={t("hl.kuvat_hitsi", { nro: h.hitsinumero })} muuttui={() => haeViimeiset()} />
                    </td>
                  </tr>
                )}
                {korjattava === h.id && (
                  <tr className="tbl__laajennus">
                    <td colSpan={5} style={{ padding: 0 }}>
                      <HitsinKorjausKentalla
                        key={h.id}
                        h={h}
                        valmis={() => { setKorjattava(null); void haeViimeiset(); }}
                        peruuta={() => setKorjattava(null)} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {/* T529: kirjaimen selite listan alla — vain listassa esiintyvät lajit, A (asennus) on oletus eikä saa merkkiä. */}
          {(() => {
            const lajit = [...new Set(viimeiset.map((h) => h.hitsityyppi).filter((x): x is NonNullable<typeof x> => !!x && x !== "A"))].sort();
            return lajit.length ? (
              <p className="hint" data-lajiselite>{lajit.map((x) => `${x} = ${lajinSelite(x)}`).join(" · ")}</p>
            ) : null;
          })()}
        </section>
      )}
    </>
  );
}

/* ── «Muu päivä…» (Toni 21.9.2026) ───────────────────────────
   Jälkikirjaus tarvitsee oman valitsimensa: kaksi nappia kattavat
   tämän ja edellisen päivän, eikä kolmatta tapausta saa jättää
   pelkän muistin varaan.

   Tulevaisuuden päivää ei tarjota — hitsiä ei ole vielä tehty. Raja on
   `max`, ei varoitus: varoituksen ohittaa se joka on kiireessä. */
function MuuPaiva({ valitse }: { valitse: (iso: string) => void }) {
  const [auki, setAuki] = useState(false);
  const [arvo, setArvo] = useState("");
  if (!auki) {
    return (
      <button className="nappi nappi--kevyt" onClick={() => setAuki(true)}>
        {t("hl.muu_paiva")}
      </button>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <input type="date" value={arvo} max={tanaan()}
             onChange={(e) => setArvo(e.target.value)} />
      <button className="nappi nappi--paa" disabled={!arvo}
              onClick={() => { valitse(arvo); setAuki(false); }}>
        {t("hl.valitse")}
      </button>
      <button className="nappi nappi--kevyt" onClick={() => setAuki(false)}>{t("hl.peruuta")}</button>
    </span>
  );
}
